import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Typography, message, Popconfirm, Tag, Badge } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import EmptyState from '../components/EmptyState';

const { Title } = Typography;

interface Form {
  id: string;
  name: string;
  website_id: string;
  submissions_count: number;
  created_at: string;
}

interface Website {
  id: string;
  domain: string;
}

const FormList: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState<Form[]>([]);
  const [websites, setWebsites] = useState<Record<string, string>>({});
  const navigate = useNavigate();

  const fetchForms = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/forms`);
      if (response.data.success) {
        setForms(response.data.data);
      } else {
        message.error(response.data.message || '获取表单列表失败');
      }
    } catch (error: any) {
      message.error('获取表单列表时出错: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchWebsites = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/seo`);
      if (response.data.success) {
        const websiteMap: Record<string, string> = {};
        response.data.data.forEach((website: Website) => {
          websiteMap[website.id] = website.domain;
        });
        setWebsites(websiteMap);
      }
    } catch (error: any) {
      message.error('获取网站列表时出错: ' + (error.response?.data?.message || error.message));
    }
  };

  useEffect(() => {
    fetchWebsites();
    fetchForms();
  }, []);

  const handleDelete = async (id: string) => {
    try {
      const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/forms/${id}`);
      if (response.data.success) {
        message.success('表单删除成功');
        fetchForms();
      } else {
        message.error(response.data.message || '删除表单失败');
      }
    } catch (error: any) {
      message.error('删除表单时出错: ' + (error.response?.data?.message || error.message));
    }
  };

  const columns = [
    {
      title: '表单名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '所属网站',
      dataIndex: 'website_id',
      key: 'website_id',
      render: (websiteId: string) => websites[websiteId] || '未知网站',
    },
    {
      title: '提交数量',
      dataIndex: 'submissions_count',
      key: 'submissions_count',
      render: (count: number) => (
        <Badge count={count} showZero color={count > 0 ? '#52c41a' : '#999'} overflowCount={999} />
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text: string) => new Date(text).toLocaleString('zh-CN'),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: Form) => (
        <Space size="middle" className="table-actions">
          <Button
            type="primary"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/forms/${record.id}/submissions`)}
          >
            查看提交
          </Button>
          <Popconfirm
            title="确定删除此表单吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="primary" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (Object.keys(websites).length === 0 && !loading) {
    return (
      <EmptyState
        title="未找到网站"
        description="请先创建一个网站，然后再添加表单"
        createPath="/websites/new"
        createText="创建网站"
      />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={2}>表单管理</Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/forms/new')}
          disabled
        >
          添加表单
        </Button>
      </div>

      <Table
        loading={loading}
        columns={columns}
        dataSource={forms}
        rowKey="id"
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: '暂无表单数据' }}
        expandable={{
          expandedRowRender: record => (
            <div>
              <p>表单ID: {record.id}</p>
              <p>嵌入代码:</p>
              <pre style={{ backgroundColor: '#f5f5f5', padding: 10, borderRadius: 4 }}>
                {`<form action="${process.env.REACT_APP_API_URL}/public/submit-form/${record.id}" method="post">
  <!-- 表单字段 -->
  <button type="submit">提交</button>
</form>`}
              </pre>
            </div>
          ),
        }}
      />
    </div>
  );
};

export default FormList; 